import CustomLoadingButton from 'Components/CustomLoadingButton'
import ServiceProviderFieldErrors from 'Components/ServiceProviderFieldErrors'
import SPMAllowedApiClients from 'Components/SPMAllowedApiClients'
import StyledLabel from 'Components/StyledLabel'
import {
  createSingleDevice,
  type Device,
  updateSingleDevice
} from 'Services/ServiceProviderManagement/Features/Device/deviceSlice'

import { SELECTED_DEVICE_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/Device/Hooks/useDevicesWithParams'
import { type LoadingStates, useAppDispatch } from 'Store'
import type {
  ServiceProviderManagementError,
  ServiceProviderManagementViolation
} from 'Util/ApiClient'

import { formatViolationErrorField } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamToUrl } from 'Util/url'
import { Box, FormControl, Grid, TextField } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  device_id: [],
  name: [],
  sales_point_id: [],
  source: []
}

interface DeviceFormProps {
  device?: Device
  successBaseUrl: string
  submitLoading: LoadingStates
}

export default function DeviceForm({
  device,
  successBaseUrl,
  submitLoading
}: Readonly<DeviceFormProps>) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isEdit = !!device

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [deviceDetails, setDeviceDetails] = useState<Partial<Device>>(
    (isEdit && device) || {
      active: true
    }
  )

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    deviceDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    deviceDetails?.client_write ?? []
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof Device
  ) => {
    if (value !== undefined) {
      setDeviceDetails(prevState => ({ ...prevState, [field]: value }) as Device)
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const deviceData = {
      ...deviceDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    if (device) {
      dispatch(
        updateSingleDevice({
          ...deviceData,
          id: device.device_id
        })
      )
        .then(unwrapResult)
        .then(() => {
          SnackbarUtils.success(t`Device successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ violations }: ServiceProviderManagementError) => {
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the device.`)
        })
      return
    }

    dispatch(createSingleDevice(deviceData))
      .then(unwrapResult)
      .then((deviceResponse: Device) => {
        SnackbarUtils.success(t`Device successfully created.`)
        navigate(
          appendQueryParamToUrl(
            successBaseUrl,
            SELECTED_DEVICE_PARAMETER_KEY,
            deviceResponse.device_id
          )
        )
      })
      .catch(({ violations, errorMessage }: ServiceProviderManagementError) => {
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (errorMessage) {
          SnackbarUtils.error(errorMessage)
        }
        SnackbarUtils.error(t`There was an error creating the device.`)
      })
  }

  function setValidationErrors(violations: ServiceProviderManagementViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const isSubmitLoading = submitLoading === 'pending'

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', mb: 4 }}>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="name"
                required
                label={t`Name`}
                size="small"
                value={deviceDetails?.name}
                slotProps={{ htmlInput: { maxLength: 40 } }}
                error={fieldErrors.name.length > 0}
                onChange={e => handleChange(e.target.value, 'name')}
              />
              <ServiceProviderFieldErrors error={fieldErrors.name} identifier="name" />
            </FormControl>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="device_id"
                required
                label={t`Device id`}
                size="small"
                value={deviceDetails?.device_id}
                slotProps={{ htmlInput: { maxLength: 5 } }}
                error={fieldErrors.device_id.length > 0}
                onChange={e => handleChange(e.target.value, 'device_id')}
              />
              <ServiceProviderFieldErrors error={fieldErrors.device_id} identifier="device_id" />
            </FormControl>
            <FormControl sx={{ minWidth: 240, mr: 3 }}>
              <TextField
                name="sales_point_id"
                required
                label={t`Sales point id`}
                size="small"
                value={deviceDetails?.sales_point_id}
                slotProps={{ htmlInput: { maxLength: 5 } }}
                error={fieldErrors.sales_point_id.length > 0}
                onChange={e => handleChange(e.target.value, 'sales_point_id')}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.sales_point_id}
                identifier="sales_point_id"
              />
            </FormControl>
          </Box>
          <Box>
            <StyledLabel>{t`Source`}</StyledLabel>
            <FormControl sx={{ minWidth: 300, mr: 3 }} size="small">
              <TextField
                name="source"
                label={t`Source`}
                size="small"
                value={deviceDetails?.source}
                error={fieldErrors.source.length > 0}
                onChange={e => handleChange(e.target.value, 'source')}
                slotProps={{ htmlInput: { maxLength: 256 } }}
              />
              <ServiceProviderFieldErrors error={fieldErrors.source} identifier="source" />
            </FormControl>
          </Box>
          <Box sx={{ maxWidth: '750px', py: 2 }}>
            <SPMAllowedApiClients
              selectedReadClients={selectedReadClients}
              selectedWriteClients={selectedWriteClients}
              setSelectedReadClients={setSelectedReadClients}
              setSelectedWriteClients={setSelectedWriteClients}
            />
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save device`}
          </CustomLoadingButton>
        </Box>
      </Grid>
    </Grid>
  )
}
